import React, { useEffect, useState } from 'react';
import {
    MdDeveloperMode, MdPowerSettingsNew, MdAdd, MdLogin, MdContentCopy, MdCheckCircle, MdBlock, MdLink, MdDeleteForever,
    MdBackup, MdRestore, MdDelete, MdWarning, MdRefresh, MdDownload
} from 'react-icons/md';
import API from '../api';
import BillingAdminCard from '../components/BillingAdminCard';
import { useLanguage, translateUiText } from '../i18n';

const BACKUP_TYPE_META = {
    manual: { label: 'Manual', bg: '#dbeafe', color: '#1d4ed8' },
    auto: { label: 'Automatic', bg: '#dcfce7', color: '#15803d' },
    pre_restore: { label: 'Pre-restore', bg: '#fef9c3', color: '#b45309' },
};

function formatBytes(bytes) {
    if (!bytes) return '—';
    const units = ['B', 'KB', 'MB', 'GB'];
    let n = bytes, i = 0;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(1)} ${units[i]}`;
}

function BackupRestoreSection() {
    const { t } = useLanguage();
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [message, setMessage] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [confirmText, setConfirmText] = useState('');

    const load = async () => {
        setLoading(true);
        try { const { data } = await API.get('/backup'); setBackups(data); }
        catch (err) { setMessage(err.response?.data?.error || translateUiText('Could not load backups')); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const createBackup = async () => {
        setCreating(true); setMessage('');
        try {
            const { data } = await API.post('/backup');
            setMessage(data.message);
            load();
        } catch (err) { setMessage(err.response?.data?.error || translateUiText('Backup failed')); }
        finally { setCreating(false); }
    };

    const downloadBackup = async backup => {
        try {
            const response = await API.get(`/backup/${backup.id}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a'); link.href = url; link.download = backup.filename; link.click(); URL.revokeObjectURL(url);
        } catch (err) { setMessage(err.response?.data?.error || translateUiText('Download failed')); }
    };

    const deleteBackup = async backup => {
        if (!window.confirm(translateUiText('Delete this backup? This cannot be undone.'))) return;
        setBusyId(backup.id);
        try { await API.delete(`/backup/${backup.id}`); load(); }
        catch (err) { setMessage(err.response?.data?.error || translateUiText('Delete failed')); }
        finally { setBusyId(null); }
    };

    const runRestore = async () => {
        if (!restoreTarget) return;
        setBusyId(restoreTarget.id); setMessage('');
        try {
            const { data } = await API.post(`/backup/${restoreTarget.id}/restore`, { confirm: confirmText });
            setMessage(data.message);
            setRestoreTarget(null); setConfirmText('');
            load();
        } catch (err) { setMessage(err.response?.data?.error || translateUiText('Restore failed')); }
        finally { setBusyId(null); }
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" onClick={createBackup} disabled={creating}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {creating ? <><span className="spinner-border spinner-border-sm me-2" />{t('developer.creatingBackup')}</> : <><MdBackup size={16} /> {t('developer.createBackupNow')}</>}
                </button>
                <button type="button" className="btn btn-light" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdRefresh size={16} /> {t('developer.refresh')}
                </button>
            </div>
            {message && <div className="alert alert-info py-2" style={{ fontSize: 13 }}>{message}</div>}

            {loading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="fms-table">
                        <thead>
                            <tr><th>{t('developer.backupTableBackup')}</th><th>{t('developer.backupTableType')}</th><th>{t('developer.backupTableSize')}</th><th>{t('developer.backupTableCreated')}</th><th style={{ width: 210 }}>{t('developer.backupTableActions')}</th></tr>
                        </thead>
                        <tbody>
                            {backups.map(b => {
                                const meta = BACKUP_TYPE_META[b.backup_type] || { label: b.backup_type, bg: '#f1f5f9', color: '#475569' };
                                return (
                                    <tr key={b.id}>
                                        <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{b.filename}</td>
                                        <td>
                                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: meta.bg, color: meta.color }}>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: 12 }}>{formatBytes(b.size_bytes)}</td>
                                        <td style={{ fontSize: 12, color: '#94a3b8' }}>
                                            {new Date(b.created_at).toLocaleString()}{b.created_by_name ? ` · ${b.created_by_name}` : ''}
                                        </td>
                                        <td>
                                            {b.status === 'failed' ? (
                                                <span style={{ color: '#b91c1c', fontSize: 12 }} title={b.error_message}>{t('developer.backupFailed')}</span>
                                            ) : (
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button type="button" className="btn btn-sm btn-light" title={t('developer.download')} onClick={() => downloadBackup(b)}>
                                                        <MdDownload size={14} />
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-warning" title={t('developer.restore')} disabled={busyId === b.id}
                                                        onClick={() => { setRestoreTarget(b); setConfirmText(''); }}>
                                                        <MdRestore size={14} /> {t('developer.restore')}
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-danger" title={t('developer.delete')} disabled={busyId === b.id}
                                                        onClick={() => deleteBackup(b)}>
                                                        <MdDelete size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!backups.length && (
                                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>
                                    {t('developer.noBackups')}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {restoreTarget && (
                <div className="modal-overlay" onClick={() => setRestoreTarget(null)}>
                    <div className="modal-box" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: 20 }}>
                            <h6 style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', marginTop: 0 }}>
                                <MdWarning /> {t('developer.restoreModalTitle')}
                            </h6>
                            <p style={{ fontSize: 13, color: '#64748b' }}>
                                {t('developer.restoreModalBody', '', { filename: restoreTarget.filename, date: new Date(restoreTarget.created_at).toLocaleString() })}
                            </p>
                            <p style={{ fontSize: 13, marginBottom: 6 }}>{t('developer.restoreModalConfirmLabel')}</p>
                            <input className="form-control" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="RESTORE" autoFocus />
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                                <button type="button" className="btn btn-light" onClick={() => setRestoreTarget(null)}>{t('developer.cancel')}</button>
                                <button type="button" className="btn btn-danger" disabled={confirmText.trim().toUpperCase() !== 'RESTORE' || busyId === restoreTarget.id}
                                    onClick={runRestore} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {busyId === restoreTarget.id ? <><span className="spinner-border spinner-border-sm me-2" />{t('developer.restoring')}</> : t('developer.restoreNow')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Developer() {
    const { t } = useLanguage();
    const [active, setActive] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [systems, setSystems] = useState([]);
    const [systemName, setSystemName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [creating, setCreating] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [newCreds, setNewCreds] = useState(null); // { name, slug, admin_email, admin_temp_password }

    const load = async () => {
        try { const [{ data }, systemsResponse] = await Promise.all([API.get('/developer/status'), API.get('/developer/systems')]); setActive(data.active); setSystems(systemsResponse.data); }
        catch (error) { setMessage(error.response?.data?.error || t('developer.loadFailed')); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggle = async () => {
        const next = !active;
        if (!next && !window.confirm(t('developer.deactivateConfirm'))) return;
        setSaving(true); setMessage('');
        try { const { data } = await API.put('/developer/status', { active: next }); setActive(data.active); setMessage(data.message); }
        catch (error) { setMessage(error.response?.data?.error || t('developer.updateFailed')); }
        finally { setSaving(false); }
    };

    const createSystem = async event => {
        event.preventDefault();
        if (!systemName.trim()) return;
        setCreating(true); setMessage(''); setNewCreds(null);
        try {
            const { data } = await API.post('/developer/systems', {
                name: systemName,
                ...(adminEmail.trim() ? { admin_email: adminEmail.trim() } : {})
            });
            setSystems(current => [{
                id: data.id, name: data.name, slug: data.slug,
                status: 'active', admin_email: data.admin_email, created_at: new Date().toISOString()
            }, ...current]);
            setNewCreds({ name: data.name, slug: data.slug, admin_email: data.admin_email, admin_temp_password: data.admin_temp_password });
            setSystemName(''); setAdminEmail('');
        } catch (error) { setMessage(error.response?.data?.error || t('developer.createSystemFailed')); }
        finally { setCreating(false); }
    };

    const openSystem = async system => {
        setBusyId(system.id); setMessage('');
        try {
            const { data } = await API.post(`/developer/systems/${system.id}/open`);
            localStorage.setItem('fms_token', data.token);
            localStorage.setItem('fms_user', JSON.stringify(data.user));
            window.location.href = '/app/dashboard';
        } catch (error) { setMessage(error.response?.data?.error || t('developer.openSystemFailed')); setBusyId(null); }
    };

    const toggleSystemStatus = async system => {
        const next = system.status !== 'active';
        if (!next && !window.confirm(t('developer.deactivateSystemConfirm', 'Deactivate "{name}"? Its users won\'t be able to sign in until it is reactivated.', { name: system.name }))) return;
        setBusyId(system.id); setMessage('');
        try {
            await API.put(`/developer/systems/${system.id}/status`, { active: next });
            setSystems(current => current.map(s => s.id === system.id ? { ...s, status: next ? 'active' : 'inactive' } : s));
        } catch (error) { setMessage(error.response?.data?.error || t('developer.updateFailed', 'Could not update system status')); }
        finally { setBusyId(null); }
    };

    const deleteSystem = async system => {
        const first = window.confirm(
            t('developer.deleteSystemConfirm',
                'Permanently delete "{name}"?\n\nThis will PERMANENTLY DESTROY its entire database — every user, space, work order, asset, booking and setting. This CANNOT be undone.\n\nDeactivating instead just blocks sign-ins and can be reversed later.',
                { name: system.name })
        );
        if (!first) return;
        const typed = window.prompt(t('developer.deleteSystemPrompt', 'To confirm permanent deletion, type the system\'s name exactly: {name}', { name: system.name }));
        if (typed !== system.name) {
            if (typed !== null) setMessage(t('developer.deleteSystemNameMismatch', 'System name did not match — deletion cancelled.'));
            return;
        }
        setBusyId(system.id); setMessage('');
        try {
            const { data } = await API.delete(`/developer/systems/${system.id}`);
            setSystems(current => current.filter(s => s.id !== system.id));
            setMessage(data.message);
        } catch (error) { setMessage(error.response?.data?.error || t('developer.deleteFailed', 'Could not delete that system')); }
        finally { setBusyId(null); }
    };

    const copy = text => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };

    const loginLink = slug => `${window.location.origin}/login?system=${slug}`;

    if (loading) return <div className="spinner-wrap"><div className="spinner-border" /></div>;

    return <div>
        <div style={{ marginBottom: 24 }}><h4 style={{ margin: 0, fontWeight: 700 }}><MdDeveloperMode style={{ color: '#7c3aed' }} /> {t('developer.title')}</h4><p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>{t('developer.subtitle')}</p></div>

        <div className="page-card" style={{ maxWidth: 680 }}>
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 12, background: active ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MdPowerSettingsNew size={30} style={{ color: active ? '#15803d' : '#b91c1c' }} /></div>
                    <div><h5 style={{ margin: 0 }}>{active ? t('developer.active') : t('developer.inactive')}</h5><p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>{active ? t('developer.available') : t('developer.developerOnly')}</p></div>
                </div>
                {message && <div className="alert alert-info">{message}</div>}
                <button className={`btn ${active ? 'btn-danger' : 'btn-success'}`} onClick={toggle} disabled={saving}>{saving ? t('developer.updating') : active ? t('developer.deactivate') : t('developer.activate')}</button>
            </div>
        </div>

        <div className="page-card" style={{ maxWidth: 900, marginTop: 20 }}>
            <div style={{ padding: 24 }}>
                <h5 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><MdBackup style={{ color: '#7c3aed' }} /> {t('developer.backupHeading')}</h5>
                <p style={{ color: '#64748b', fontSize: 13 }}>
                    {t('developer.backupIntro')}
                </p>
                <BillingAdminCard />
                <BackupRestoreSection />
            </div>
        </div>

        <div className="page-card" style={{ maxWidth: 680, marginTop: 20 }}>
            <div style={{ padding: 24 }}>
                <h5 style={{ marginTop: 0 }}>{t('developer.systemsHeading')}</h5>
                <p style={{ color: '#64748b', fontSize: 13 }}>
                    {t('developer.systemsIntro')}
                </p>

                <form onSubmit={createSystem} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input className="form-control" style={{ flex: '1 1 220px', minWidth: 180 }} value={systemName} onChange={event => setSystemName(event.target.value)} placeholder={t('developer.newSystemNamePlaceholder')} disabled={creating} />
                    <input className="form-control" type="email" style={{ flex: '1 1 220px', minWidth: 180 }} value={adminEmail} onChange={event => setAdminEmail(event.target.value)} placeholder={t('developer.adminEmailPlaceholder')} disabled={creating} />
                    <button className="btn btn-primary" type="submit" disabled={creating}><MdAdd /> {creating ? t('developer.creatingSystem') : t('developer.createSystem')}</button>
                </form>

                {newCreds && (
                    <div style={{ marginTop: 16, padding: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#15803d', marginBottom: 8 }}>
                            <MdCheckCircle /> {t('developer.credsReady', '', { name: newCreds.name })}
                        </div>
                        <div style={{ fontSize: 13, fontFamily: 'monospace', background: '#fff', border: '1px solid #d1fae5', borderRadius: 6, padding: '10px 12px', lineHeight: 1.8 }}>
                            <div>{t('developer.credsAdminEmail')} <b>{newCreds.admin_email}</b></div>
                            <div>{t('developer.credsTempPassword')} <b>{newCreds.admin_temp_password}</b>
                                <button className="btn btn-sm btn-link" style={{ padding: '0 0 0 8px' }} onClick={() => copy(newCreds.admin_temp_password)} title={t('developer.copyPassword')}><MdContentCopy /></button>
                            </div>
                            <div>{t('developer.credsSignInLink')} <b>{loginLink(newCreds.slug)}</b>
                                <button className="btn btn-sm btn-link" style={{ padding: '0 0 0 8px' }} onClick={() => copy(loginLink(newCreds.slug))} title={t('developer.copyLink')}><MdContentCopy /></button>
                            </div>
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" style={{ marginTop: 10 }} onClick={() => setNewCreds(null)}>{t('developer.dismiss')}</button>
                    </div>
                )}

                <div style={{ marginTop: 16 }}>
                    {systems.length === 0 && <p style={{ color: '#94a3b8', fontSize: 13 }}>{t('developer.noSystems')}</p>}
                    {systems.map(system => (
                        <div key={system.id} style={{ marginTop: 10, padding: '12px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 160 }}>
                                <div style={{ fontWeight: 700 }}>{system.name} <span style={{ color: '#94a3b8', fontWeight: 400 }}>/{system.slug}</span></div>
                                {system.admin_email && <div style={{ color: '#64748b', fontSize: 12 }}>{system.admin_email}</div>}
                            </div>
                            <span className={`badge ${system.status === 'active' ? 'bg-success' : 'bg-secondary'}`} style={{ fontWeight: 600 }}>{system.status === 'active' ? t('developer.systemActive') : t('developer.systemDeactivated')}</span>
                            <button className="btn btn-sm btn-outline-secondary" title={t('developer.copySignInLink')} onClick={() => copy(loginLink(system.slug))}><MdLink /></button>
                            <button className="btn btn-sm btn-primary" disabled={busyId === system.id || system.status !== 'active'} onClick={() => openSystem(system)}><MdLogin /> {t('developer.openButton')}</button>
                            <button className={`btn btn-sm ${system.status === 'active' ? 'btn-outline-danger' : 'btn-outline-success'}`} disabled={busyId === system.id} onClick={() => toggleSystemStatus(system)}>
                                {system.status === 'active' ? <><MdBlock /> {t('developer.deactivateButton')}</> : <><MdCheckCircle /> {t('developer.activateButton')}</>}
                            </button>
                            <button className="btn btn-sm btn-danger" disabled={busyId === system.id} title={t('developer.deleteSystemTitle')} onClick={() => deleteSystem(system)}>
                                <MdDeleteForever /> {t('developer.deleteButton')}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>;
}
